/**
 * MemberThemeToggle — the member portal's quick light/dark switch for the
 * sidebar footer. A thin wrapper around the same mechanism as the staff
 * PublicThemeToggle (toggles the `dark` class on <html> + persists to
 * localStorage('theme')), but ALSO persists to the server via
 * updateMemberPreferences so the choice follows the member across devices.
 *
 * Styled to match the MemberSidebar footer buttons so it reads as native nav.
 */
import { useEffect, useState } from 'react';
import { Moon, Sun } from 'lucide-react';
import {
  applyTheme,
  persistThemeLocal,
  updateMemberPreferences,
} from '@/lib/memberPreferences';

function isCurrentlyDark(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
}

export function MemberThemeToggle({ collapsed = false }: { collapsed?: boolean }) {
  const [isDark, setIsDark] = useState<boolean>(isCurrentlyDark);

  // Re-sync with whatever the layout/boot script applied on mount.
  useEffect(() => {
    setIsDark(isCurrentlyDark());
  }, []);

  const toggle = () => {
    const next = !isDark;
    const theme = next ? 'dark' : 'light';
    setIsDark(next);
    applyTheme(theme);
    persistThemeLocal(theme);
    // Fire-and-forget: local state already reflects the change.
    updateMemberPreferences({ theme }).catch(() => {
      /* offline / not-authed — localStorage still holds the choice */
    });
  };

  const label = isDark ? 'Light mode' : 'Dark mode';

  return (
    <button
      onClick={toggle}
      aria-label={label}
      title={collapsed ? label : undefined}
      className={`flex w-full items-center gap-2.5 rounded-[var(--v2-r-sm)] px-[9px] py-[7px] text-[13px] font-medium text-ink-700 transition-colors hover:bg-paper-2 hover:text-ink ${
        collapsed ? 'justify-center' : ''
      }`}
    >
      {isDark ? (
        <Sun size={14} className="flex-shrink-0" />
      ) : (
        <Moon size={14} className="flex-shrink-0" />
      )}
      {!collapsed && <span className="flex-1 text-left">{label}</span>}
    </button>
  );
}

export default MemberThemeToggle;
